import React, { useEffect, useState } from "react";
import {
  fetchFinanceZReport,
  fetchDocumentReport,
  fetchSalesReport,
  fetchVatReport,
  voidDocument,
  getCurrency,
  getRole,
  refreshMe,
  updateRestaurantSettings,
  exportAccountingCsv,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { printDocument } from "../utils/printDoc";
import { useT } from "../i18n/useT";

// Finance reports (S1) — one page over the read-only `postings` ledger.
// Three views (Z-report / Documents / Sales) for one business day, for the
// hotel manager or cashier. Mirrors FrontDeskReportsPage: local trilingual
// `S` table, CARD/TH/TR styling, tabbed sub-sections, formatPrice for money.
const S = {
  en: {
    title: "Finance reports",
    subtitle: "Daily fiscal views over the revenue ledger",
    date: "Business day",
    open_note: "This business day is still OPEN — figures may still change.",
    closed_note: "This business day is CLOSED.",
    timeframe: "Timeframe",
    tf_today: "Today", tf_week: "This week", tf_month: "This month", tf_year: "This year", tf_custom: "Custom",
    from_l: "From", to_l: "To", range_l: "Range",
    print: "Print", acct_export: "For accountant (CSV)",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm",
    t_z: "Z-Report", t_docs: "Documents", t_sales: "Sales", t_vat: "VAT / Tax",
    none: "Nothing recorded for this day.",
    taxable: "Taxable base", exempt: "Exempt / zero", tax_collected: "VAT collected", vat_rate: "VAT rate",
    // Z-report
    by_point: "Revenue by point", point: "Point",
    net: "Net", vat: "VAT", service: "Service", gross: "Gross", count: "Docs",
    totals: "Totals", by_method: "Collections by payment method",
    method: "Method", amount: "Amount",
    settled_credit: "Settled vs credit",
    paid_gross: "Settled (paid)", credit_gross: "On credit (this day)",
    new_credit: "New credit today", outstanding: "Outstanding credit (all days)",
    // Documents
    doc_no: "No.", time: "Time", doc_type: "Type", guest: "Guest", tin: "TIN",
    state: "State", voided: "Voided", cashier: "Cashier", yes: "Yes",
    void_btn: "Void", void_prompt: "Reason for voiding this document?",
    void_done: "Document voided.",
    void_already: "This document is already voided.",
    void_unavailable: "Couldn't void this document. Please try again in a moment.",
    // Sales
    cashier_id: "Cashier ID", scope: "Scope", scope_day: "Whole day",
    scope_shift: "This cashier", run: "Run", all_cashiers: "All cashiers",
    rev_point: "Revenue point", lines: "Line detail",
    sales_point: "Sales point", pt_all: "All points", pt_front_desk: "Front desk", pt_fnb: "Food & Beverage", pt_wellness: "Wellness", pt_events: "Events",
  },
  am: {
    title: "የፋይናንስ ሪፖርቶች",
    subtitle: "የዕለት የገቢ መዝገብ የፊስካል እይታዎች",
    date: "የስራ ቀን",
    open_note: "ይህ የስራ ቀን ገና ክፍት ነው — አሃዞች ሊቀየሩ ይችላሉ።",
    closed_note: "ይህ የስራ ቀን ተዘግቷል።",
    timeframe: "የጊዜ ገደብ",
    tf_today: "ዛሬ", tf_week: "ይህ ሳምንት", tf_month: "ይህ ወር", tf_year: "ይህ ዓመት", tf_custom: "ብጁ",
    from_l: "ከ", to_l: "እስከ", range_l: "ክልል", acct_export: "ለሒሳብ ባለሙያ (CSV)",
    print: "አትም",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm", // TODO verify
    t_z: "Z-ሪፖርት", t_docs: "ሰነዶች", t_sales: "ሽያጭ", t_vat: "ተ.እ.ታ / ግብር",
    taxable: "ታክስ የሚከፈልበት", exempt: "ነፃ / ዜሮ", tax_collected: "የተሰበሰበ ተ.እ.ታ", vat_rate: "የተ.እ.ታ ምጣኔ",
    none: "በዚህ ቀን የተመዘገበ የለም።",
    by_point: "ገቢ በነጥብ", point: "ነጥብ",
    net: "ተጣራ", vat: "ታክስ", service: "አገልግሎት", gross: "ጠቅላላ", count: "ሰነዶች",
    totals: "ድምሮች", by_method: "ክፍያ በዘዴ",
    method: "ዘዴ", amount: "መጠን",
    settled_credit: "የተከፈለ ከዕዳ ጋር",
    paid_gross: "የተከፈለ", credit_gross: "በዕዳ (በዚህ ቀን)",
    new_credit: "ዛሬ አዲስ ዕዳ", outstanding: "ያልተከፈለ ዕዳ (ሁሉም ቀናት)",
    doc_no: "ቁ.", time: "ሰዓት", doc_type: "ዓይነት", guest: "እንግዳ", tin: "ቲን",
    state: "ሁኔታ", voided: "የተሰረዘ", cashier: "ገንዘብ ተቀባይ", yes: "አዎ",
    void_btn: "ሰርዝ", void_prompt: "ይህን ሰነድ ለመሰረዝ ምክንያት?",
    void_done: "ሰነዱ ተሰርዟል።",
    void_already: "ይህ ሰነድ አስቀድሞ ተሰርዟል።",
    void_unavailable: "ሰነዱን መሰረዝ አልተቻለም። እባክዎ ትንሽ ቆይተው እንደገና ይሞክሩ።",
    cashier_id: "የገንዘብ ተቀባይ መለያ", scope: "ወሰን", scope_day: "ሙሉ ቀን",
    scope_shift: "ይህ ገንዘብ ተቀባይ", run: "አሂድ", all_cashiers: "ሁሉም ገንዘብ ተቀባዮች",
    rev_point: "የገቢ ነጥብ", lines: "ዝርዝር",
    sales_point: "የሽያጭ ነጥብ", pt_all: "ሁሉም ነጥቦች", pt_front_desk: "የፊት ዴስክ", pt_fnb: "ምግብና መጠጥ", pt_wellness: "ጤና", pt_events: "ዝግጅቶች",
  },
  om: {
    title: "Gabaasota faayinaansii",
    subtitle: "Ilaalcha fiscal guyyaa galmee galii irratti",
    date: "Guyyaa hojii",
    open_note: "Guyyaan hojii kun ammallee BANAA dha — lakkoofsonni jijjiiramuu danda'u.",
    closed_note: "Guyyaan hojii kun CUFAME.",
    timeframe: "Yeroo",
    tf_today: "Har'a", tf_week: "Torban kana", tf_month: "Ji'a kana", tf_year: "Waggaa kana", tf_custom: "Kan mataa",
    from_l: "Irraa", to_l: "Hanga", range_l: "Daangaa", acct_export: "Herregdheessaaf (CSV)",
    print: "Maxxansi",
    print_format: "Print format", fmt_a4: "A4", fmt_thermal: "Thermal 80mm", // TODO verify
    t_z: "Z-Gabaasa", t_docs: "Sanadoota", t_sales: "Gurgurtaa", t_vat: "VAT / Gibira",
    taxable: "Bu'uura gibiraa", exempt: "Bilisa / zeeroo", tax_collected: "VAT walitti qabame", vat_rate: "Reeti VAT",
    none: "Guyyaa kanaaf homaa hin galmoofne.",
    by_point: "Galii bakka galiitiin", point: "Bakka",
    net: "Qulqulluu", vat: "Gibira", service: "Tajaajila", gross: "Waliigala", count: "Sanadoota",
    totals: "Ida'amoota", by_method: "Kaffaltii mala kaffaltiitiin",
    method: "Mala", amount: "Hanga",
    settled_credit: "Kaffalame vs liqii",
    paid_gross: "Kaffalame", credit_gross: "Liqiidhaan (guyyaa kana)",
    new_credit: "Liqii har'aa haaraa", outstanding: "Liqii hafe (guyyaa hunda)",
    doc_no: "Lakk.", time: "Sa'aa", doc_type: "Gosa", guest: "Keessummaa", tin: "TIN",
    state: "Haala", voided: "Haqame", cashier: "Kaasherii", yes: "Eeyyee",
    void_btn: "Haqi", void_prompt: "Sababa sanada kana haquuf?",
    void_done: "Sanadni haqameera.",
    void_already: "Sanadni kun duraanuu haqameera.",
    void_unavailable: "Sanada kana haquun hin danda'amne. Maaloo yeroo xiqqoo booda irra deebi'aa yaalaa.",
    cashier_id: "ID Kaasherii", scope: "Hangi", scope_day: "Guyyaa guutuu",
    scope_shift: "Kaasherii kana", run: "Fiigi", all_cashiers: "Kaasheroota hunda",
    rev_point: "Bakka galii", lines: "Bal'ina sararaa",
    sales_point: "Bakka gurgurtaa", pt_all: "Bakkeewwan hunda", pt_front_desk: "Fuula duraa", pt_fnb: "Nyaataa fi Dhugaatii", pt_wellness: "Fayyaa", pt_events: "Taatewwan",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TH = { textAlign: "left", color: "#5b6472", padding: "3px 6px" };
const TR = { textAlign: "right" };
const iso = (off) => { const d = new Date(); d.setDate(d.getDate() + off); return d.toISOString().slice(0, 10); };
// Monday of the current week (Mon → Sun; Mon=0).
const mondayIso = () => { const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10); };
const firstOfMonthIso = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const firstOfYearIso = () => `${new Date().getFullYear()}-01-01`;
// Resolve a preset (or custom from/to) into a concrete {from, to} range.
const presetRange = (preset, cFrom, cTo) => {
  const today = iso(0);
  switch (preset) {
    case "week": return { from: mondayIso(), to: today };
    case "month": return { from: firstOfMonthIso(), to: today };
    case "year": return { from: firstOfYearIso(), to: today };
    case "custom": return { from: cFrom, to: cTo };
    case "today":
    default: return { from: today, to: today };
  }
};

export default function FinanceReportsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);

  const [preset, setPreset] = useState("today");
  const [customFrom, setCustomFrom] = useState(iso(0));
  const [customTo, setCustomTo] = useState(iso(0));
  const { from, to } = presetRange(preset, customFrom, customTo);
  const rangeLabel = from === to ? from : `${from} – ${to}`;
  const [tab, setTab] = useState("z");
  const [printFormat, setPrintFormat] = useState("a4");
  const [cashierId, setCashierId] = useState("");
  const [point, setPoint] = useState("");
  const [scope, setScope] = useState("day");

  const [fiscalProvider, setFiscalProvider] = useState(null);   // e-invoicing dry-run toggle state
  useEffect(() => { refreshMe().then((me) => setFiscalProvider(me.fiscal_provider || null)).catch(() => {}); }, []);
  const toggleFiscalMock = async () => {
    const next = fiscalProvider === "mock" ? "none" : "mock";
    setFiscalProvider(next === "mock" ? "mock" : null);
    try { await updateRestaurantSettings({ fiscal_provider: next }); } catch { setFiscalProvider(fiscalProvider); }
  };
  const [z, setZ] = useState(null);
  const [docs, setDocs] = useState(null);
  const [sales, setSales] = useState(null);
  const [vat, setVat] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setErr("");
    setLoading(true);
    setZ(null); setDocs(null); setSales(null); setVat(null);
    Promise.all([
      fetchFinanceZReport(undefined, { from, to }).then(setZ).catch(() => setZ({})),
      fetchDocumentReport(undefined, { from, to }).then(setDocs).catch(() => setDocs({})),
      fetchSalesReport({ from, to, cashierId: cashierId ? Number(cashierId) : undefined, scope, point: point || undefined })
        .then(setSales).catch(() => setSales({})),
      fetchVatReport({ from, to }).then(setVat).catch(() => setVat({})),
    ]).catch(() => setErr("Could not load reports.")).finally(() => setLoading(false));
  };
  // Re-load whenever the timeframe changes; cashier/scope re-run via button.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset, from, to]);

  // Void a whole document (all its lines). Correction path: nothing is edited or
  // deleted — the ledger flags it voided with a reason and who/when.
  const doVoidDoc = async (r) => {
    const reason = window.prompt(t("void_prompt"));
    if (!reason || !reason.trim()) return;
    try { await voidDocument(r.document_number, reason.trim()); load(); }
    catch (e) {
      // Friendly, plain-language messages instead of raw server text. 409 =
      // already voided; anything else (incl. a 404 during a deploy window) =
      // generic "try again".
      const s = e?.response?.status;
      alert(t(s === 409 ? "void_already" : "void_unavailable"));
    }
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
  const tabBtn = (k) => ({
    padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13.5, fontWeight: tab === k ? 700 : 500,
    border: tab === k ? "1.5px solid #0F6E56" : "1px solid #e2e6ea", background: tab === k ? "#E7F6EC" : "#fff",
    color: tab === k ? "#0F6E56" : "#5b6472",
  });

  // Print the active view via the shared print helper, sized for the chosen
  // device (A4 management sheet or 80mm thermal receipt roll).
  const printCurrent = () => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const tbl = (headers, rows) =>
      `<table style="width:100%;border-collapse:collapse;font-size:12px;margin:6px 0">`
      + `<thead><tr>${headers.map((h) => `<th style="text-align:left;border-bottom:1px solid #999;padding:3px 6px">${esc(h)}</th>`).join("")}</tr></thead>`
      + `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td style="padding:2px 6px;border-bottom:1px solid #eee">${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    let body = "";
    let heading = "";
    if (tab === "z" && z) {
      heading = t("t_z");
      body += `<h3>${esc(t("by_point"))}</h3>`
        + tbl([t("point"), t("net"), t("vat"), t("service"), t("gross"), t("count")],
          (z.by_revenue_point || []).map((p) => [p.point, money(p.net), money(p.vat), money(p.service), money(p.gross), p.count]));
      const tot = z.totals || {};
      body += `<h3>${esc(t("totals"))}</h3>`
        + tbl([t("net"), t("vat"), t("service"), t("gross"), t("count")],
          [[money(tot.net), money(tot.vat), money(tot.service), money(tot.gross), tot.count]]);
      body += `<h3>${esc(t("by_method"))}</h3>`
        + tbl([t("method"), t("amount")], (z.by_payment_method || []).map((m) => [m.method, money(m.amount)]));
      const sc = z.settled_vs_credit || {};
      body += `<h3>${esc(t("settled_credit"))}</h3>`
        + tbl([t("paid_gross"), t("credit_gross"), t("new_credit"), t("outstanding")],
          [[money(sc.paid_gross), money(sc.credit_gross), money(z.new_credit_today), money(z.outstanding_credit_total)]]);
    } else if (tab === "docs" && docs) {
      heading = t("t_docs");
      body += tbl([t("doc_no"), t("time"), t("guest"), t("net"), t("vat"), t("gross"), t("method"), t("state"), t("voided")],
        (docs.documents || []).map((r) => [
          r.document_number || "-", (r.time || "").slice(11, 16), r.guest_name || "-",
          money(r.net), money(r.vat), money(r.gross), r.payment_method || "-",
          r.settlement_state || "-", r.voided ? t("yes") : "",
        ]));
    } else if (tab === "sales" && sales) {
      heading = t("t_sales");
      body += tbl([t("time"), t("doc_no"), t("rev_point"), t("guest"), t("gross"), t("method"), t("state")],
        (sales.lines || []).map((r) => [
          (r.time || "").slice(11, 16), r.document_number || "-", r.revenue_point || "-",
          r.guest_name || "-", money(r.gross), r.payment_method || "-", r.settlement_state || "-",
        ]));
    } else if (tab === "vat" && vat) {
      heading = t("t_vat");
      body += tbl([t("point"), t("taxable"), t("exempt"), t("tax_collected"), t("gross"), t("count")],
        (vat.by_revenue_point || []).map((p) => [p.point, money(p.taxable_net), money(p.exempt_net), money(p.vat), money(p.gross), p.count]));
      const vt = vat.totals || {};
      body += `<h3>${esc(t("totals"))}</h3>`
        + tbl([t("taxable"), t("exempt"), t("tax_collected"), t("gross")],
          [[money(vt.taxable_net), money(vt.exempt_net), money(vt.vat), money(vt.gross)]]);
    }
    const bodyHtml =
      `<h2>${esc(t("title"))} — ${esc(heading)}</h2>`
      + `<div style="color:#666;margin-bottom:8px">${esc(t("range_l"))}: ${esc(rangeLabel)}</div>${body}`;
    printDocument({
      title: `${t("title")} — ${heading} ${rangeLabel}`,
      bodyHtml,
      format: printFormat,
    });
  };

  const z_empty = z && (!z.totals || !z.totals.count);
  const docs_empty = docs && (!docs.documents || docs.documents.length === 0);
  const sales_empty = sales && (!sales.lines || sales.lines.length === 0);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("timeframe")}{" "}
          <select style={{ ...inp, marginLeft: 4 }} value={preset} onChange={(e) => setPreset(e.target.value)}>
            <option value="today">{t("tf_today")}</option>
            <option value="week">{t("tf_week")}</option>
            <option value="month">{t("tf_month")}</option>
            <option value="year">{t("tf_year")}</option>
            <option value="custom">{t("tf_custom")}</option>
          </select>
        </label>
        {preset === "custom" && (
          <>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from_l")}{" "}
              <input type="date" style={{ ...inp, marginLeft: 4 }} value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to_l")}{" "}
              <input type="date" style={{ ...inp, marginLeft: 4 }} value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)} />
            </label>
          </>
        )}
        <span style={{ fontSize: 12.5, color: "#5f6b78" }}>{t("range_l")}: <strong style={{ color: "#0F6E56" }}>{rangeLabel}</strong></span>
        <span style={{ flex: 1 }} />
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("print_format")}{" "}
          <select style={{ ...inp, marginLeft: 4 }} value={printFormat} onChange={(e) => setPrintFormat(e.target.value)}>
            <option value="a4">{t("fmt_a4")}</option>
            <option value="thermal">{t("fmt_thermal")}</option>
          </select>
        </label>
        <button className="btn secondary small" onClick={printCurrent}>{t("print")}</button>
        {getRole() === "manager" && (
          <button className="btn secondary small" onClick={() => exportAccountingCsv(from, to)} title="Daily revenue by category + VAT + collections, for your accountant">
            ⬇ {t("acct_export")}
          </button>
        )}
      </div>

      {/* Day open / closed note — read from the Z-report is_open flag. */}
      {z && z.is_open != null && (
        <div style={{
          marginTop: 8, fontSize: 13, fontWeight: 600, padding: "6px 12px", borderRadius: 8,
          display: "inline-block",
          background: z.is_open ? "#FDF6E3" : "#E7F3EE",
          color: z.is_open ? "#8A5A0B" : "#0F6E56",
        }}>
          {z.is_open ? t("open_note") : t("closed_note")}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button style={tabBtn("z")} onClick={() => setTab("z")}>{t("t_z")}</button>
        <button style={tabBtn("docs")} onClick={() => setTab("docs")}>{t("t_docs")}</button>
        <button style={tabBtn("sales")} onClick={() => setTab("sales")}>{t("t_sales")}</button>
        <button style={tabBtn("vat")} onClick={() => setTab("vat")}>{t("t_vat")}</button>
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {loading && !z ? <p className="muted" style={{ marginTop: 14 }}>...</p> : (
        <div style={{ marginTop: 12 }}>
          {/* ---------------- Z-REPORT ---------------- */}
          {tab === "z" && (
            z == null ? <p className="muted">...</p> :
            z_empty ? <div style={{ ...CARD, padding: 14 }}><div className="muted" style={{ fontSize: 13 }}>{t("none")}</div></div> : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
                <div style={{ ...CARD, padding: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("by_point")}</div>
                  <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={TH}>{t("point")}</th>
                      <th style={{ ...TH, ...TR }}>{t("net")}</th>
                      <th style={{ ...TH, ...TR }}>{t("vat")}</th>
                      <th style={{ ...TH, ...TR }}>{t("service")}</th>
                      <th style={{ ...TH, ...TR }}>{t("gross")}</th>
                      <th style={{ ...TH, ...TR }}>{t("count")}</th>
                    </tr></thead>
                    <tbody>
                      {(z.by_revenue_point || []).map((p) => (
                        <tr key={p.point}>
                          <td style={{ padding: "3px 6px", fontWeight: 600 }}>{p.point}</td>
                          <td style={TR}>{money(p.net)}</td>
                          <td style={TR}>{money(p.vat)}</td>
                          <td style={TR}>{money(p.service)}</td>
                          <td style={TR}>{money(p.gross)}</td>
                          <td style={TR}>{p.count}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                        <td style={{ padding: "4px 6px" }}>{t("totals")}</td>
                        <td style={TR}>{money((z.totals || {}).net)}</td>
                        <td style={TR}>{money((z.totals || {}).vat)}</td>
                        <td style={TR}>{money((z.totals || {}).service)}</td>
                        <td style={TR}>{money((z.totals || {}).gross)}</td>
                        <td style={TR}>{(z.totals || {}).count}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ ...CARD, padding: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("by_method")}</div>
                  {(z.by_payment_method || []).length === 0 ? (
                    <div className="muted" style={{ fontSize: 13 }}>{t("none")}</div>
                  ) : (
                    <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                      <thead><tr><th style={TH}>{t("method")}</th><th style={{ ...TH, ...TR }}>{t("amount")}</th></tr></thead>
                      <tbody>
                        {z.by_payment_method.map((m) => (
                          <tr key={m.method}>
                            <td style={{ padding: "3px 6px", fontWeight: 600 }}>{m.method}</td>
                            <td style={TR}>{money(m.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div style={{ fontWeight: 700, margin: "14px 0 6px" }}>{t("settled_credit")}</div>
                  <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                    <tbody>
                      <tr><td style={{ padding: "3px 6px", color: "#0F6E56" }}>{t("paid_gross")}</td><td style={{ ...TR, color: "#0F6E56" }}>{money((z.settled_vs_credit || {}).paid_gross)}</td></tr>
                      <tr><td style={{ padding: "3px 6px", color: "#8A5A0B" }}>{t("credit_gross")}</td><td style={{ ...TR, color: "#8A5A0B" }}>{money((z.settled_vs_credit || {}).credit_gross)}</td></tr>
                      <tr><td style={{ padding: "3px 6px" }}>{t("new_credit")}</td><td style={TR}>{money(z.new_credit_today)}</td></tr>
                    </tbody>
                  </table>

                  {/* Outstanding-credit callout — money owed across all days. */}
                  <div style={{ marginTop: 12, background: "#FDECEC", border: "1px solid #F3C9C9", borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "#A32D2D", fontWeight: 600 }}>{t("outstanding")}</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: "#A32D2D" }}>{money(z.outstanding_credit_total)}</span>
                  </div>
                </div>
              </div>
            )
          )}

          {/* ---------------- DOCUMENTS ---------------- */}
          {tab === "docs" && (
            docs == null ? <p className="muted">...</p> :
            (
              <>
              {getRole() === "manager" && (
                <div style={{ ...CARD, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#063b48" }}>E-invoicing</span>
                  <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={fiscalProvider === "mock"} onChange={toggleFiscalMock} />
                    Dry-run (mock) — stamp a simulated IRN + QR on new invoices to preview e-invoicing
                  </label>
                  {fiscalProvider === "mock" && <span style={{ fontSize: 12, color: "#8A5A0B" }}>Test mode — not a real fiscal document.</span>}
                </div>
              )}
              {docs_empty ? <div style={{ ...CARD, padding: 14 }}><div className="muted" style={{ fontSize: 13 }}>{t("none")}</div></div> : (
              <div style={{ ...CARD, padding: 14, overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={TH}>{t("doc_no")}</th>
                    <th style={TH}>{t("time")}</th>
                    <th style={TH}>{t("guest")}</th>
                    <th style={{ ...TH, ...TR }}>{t("net")}</th>
                    <th style={{ ...TH, ...TR }}>{t("vat")}</th>
                    <th style={{ ...TH, ...TR }}>{t("service")}</th>
                    <th style={{ ...TH, ...TR }}>{t("gross")}</th>
                    <th style={TH}>{t("method")}</th>
                    <th style={TH}>{t("state")}</th>
                    <th style={TH}>{t("voided")}</th>
                    <th style={TH}>E-invoice</th>
                    <th style={TH}></th>
                  </tr></thead>
                  <tbody>
                    {docs.documents.map((r, i) => (
                      <tr key={i} style={{ opacity: r.voided ? 0.5 : 1, textDecoration: r.voided ? "line-through" : "none" }}>
                        <td style={{ padding: "3px 6px", fontWeight: 600 }}>{r.document_number || "-"}</td>
                        <td style={{ padding: "3px 6px" }}>{(r.time || "").slice(11, 16)}</td>
                        <td style={{ padding: "3px 6px" }}>{r.guest_name || "-"}</td>
                        <td style={TR}>{money(r.net)}</td>
                        <td style={TR}>{money(r.vat)}</td>
                        <td style={TR}>{money(r.service)}</td>
                        <td style={TR}>{money(r.gross)}</td>
                        <td style={{ padding: "3px 6px" }}>{r.payment_method || "-"}</td>
                        <td style={{ padding: "3px 6px" }}>{r.settlement_state || "-"}</td>
                        <td style={{ padding: "3px 6px", color: "#A32D2D" }}>{r.voided ? t("yes") : ""}</td>
                        <td style={{ padding: "3px 6px", fontSize: 11, color: r.fiscal_irn ? "#1D7A44" : "#8a949b" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {r.fiscal_qr_image && <img src={r.fiscal_qr_image} alt="QR" title={r.fiscal_irn || ""} style={{ width: 34, height: 34, borderRadius: 3 }} />}
                            <span>{r.fiscal_irn || (r.fiscal_status ? r.fiscal_status : "—")}</span>
                          </div>
                        </td>
                        <td style={{ padding: "3px 6px" }}>
                          {!r.voided && r.document_number && (
                            <button onClick={() => doVoidDoc(r)}
                              style={{ fontSize: 11, padding: "2px 8px", border: "1px solid #A32D2D", color: "#A32D2D", background: "#fff", borderRadius: 6, cursor: "pointer" }}>
                              {t("void_btn")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {docs.totals && (
                      <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                        <td style={{ padding: "4px 6px" }} colSpan={3}>{t("totals")} ({docs.totals.count})</td>
                        <td style={TR}>{money(docs.totals.net)}</td>
                        <td style={TR}>{money(docs.totals.vat)}</td>
                        <td style={TR}>{money(docs.totals.service)}</td>
                        <td style={TR}>{money(docs.totals.gross)}</td>
                        <td colSpan={4} />
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
              </>
            )
          )}

          {/* ---------------- SALES ---------------- */}
          {tab === "sales" && (
            <div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("sales_point")}<br />
                  <select style={{ ...inp }} value={point} onChange={(e) => setPoint(e.target.value)}>
                    <option value="">{t("pt_all")}</option>
                    <option value="front_desk">{t("pt_front_desk")}</option>
                    <option value="fnb">{t("pt_fnb")}</option>
                    <option value="wellness">{t("pt_wellness")}</option>
                    <option value="events">{t("pt_events")}</option>
                  </select>
                </label>
                <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("cashier_id")}<br />
                  <input type="number" min="1" style={{ ...inp, width: 130 }} value={cashierId}
                    onChange={(e) => setCashierId(e.target.value)} placeholder={t("all_cashiers")} />
                </label>
                <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("scope")}<br />
                  <select style={{ ...inp }} value={scope} onChange={(e) => setScope(e.target.value)}>
                    <option value="day">{t("scope_day")}</option>
                    <option value="shift">{t("scope_shift")}</option>
                  </select>
                </label>
                <button className="btn primary small" onClick={load}>{t("run")}</button>
              </div>

              {sales == null ? <p className="muted">...</p> :
                sales_empty ? <div style={{ ...CARD, padding: 14 }}><div className="muted" style={{ fontSize: 13 }}>{t("none")}</div></div> : (
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ ...CARD, padding: 14, overflowX: "auto" }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("lines")}</div>
                      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                        <thead><tr>
                          <th style={TH}>{t("time")}</th>
                          <th style={TH}>{t("doc_no")}</th>
                          <th style={TH}>{t("rev_point")}</th>
                          <th style={TH}>{t("guest")}</th>
                          <th style={{ ...TH, ...TR }}>{t("gross")}</th>
                          <th style={TH}>{t("method")}</th>
                          <th style={TH}>{t("state")}</th>
                        </tr></thead>
                        <tbody>
                          {sales.lines.map((r, i) => (
                            <tr key={i}>
                              <td style={{ padding: "3px 6px" }}>{(r.time || "").slice(11, 16)}</td>
                              <td style={{ padding: "3px 6px" }}>{r.document_number || "-"}</td>
                              <td style={{ padding: "3px 6px" }}>{r.revenue_point || "-"}</td>
                              <td style={{ padding: "3px 6px" }}>{r.guest_name || "-"}</td>
                              <td style={TR}>{money(r.gross)}</td>
                              <td style={{ padding: "3px 6px" }}>{r.payment_method || "-"}</td>
                              <td style={{ padding: "3px 6px" }}>{r.settlement_state || "-"}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                            <td style={{ padding: "4px 6px" }} colSpan={4}>{t("totals")} ({(sales.totals || {}).count})</td>
                            <td style={TR}>{money((sales.totals || {}).gross)}</td>
                            <td colSpan={2} />
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                      <div style={{ ...CARD, padding: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("by_method")}</div>
                        <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                          <thead><tr><th style={TH}>{t("method")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("amount")}</th></tr></thead>
                          <tbody>
                            {(sales.by_payment_method || []).map((m) => (
                              <tr key={m.method}>
                                <td style={{ padding: "3px 6px", fontWeight: 600 }}>{m.method}</td>
                                <td style={TR}>{m.count}</td>
                                <td style={TR}>{money(m.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ ...CARD, padding: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("by_point")}</div>
                        <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                          <thead><tr><th style={TH}>{t("rev_point")}</th><th style={{ ...TH, ...TR }}>{t("count")}</th><th style={{ ...TH, ...TR }}>{t("gross")}</th></tr></thead>
                          <tbody>
                            {(sales.by_revenue_point || []).map((p) => (
                              <tr key={p.point}>
                                <td style={{ padding: "3px 6px", fontWeight: 600 }}>{p.point}</td>
                                <td style={TR}>{p.count}</td>
                                <td style={TR}>{money(p.gross)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* ---------------- VAT / TAX ---------------- */}
          {tab === "vat" && (
            vat == null ? <p className="muted">...</p> :
            (!vat.totals || !vat.totals.count) ? <div style={{ ...CARD, padding: 14 }}><div className="muted" style={{ fontSize: 13 }}>{t("none")}</div></div> : (
              <div style={{ display: "grid", gap: 12 }}>
                {vat.vat_pct != null && (
                  <div className="muted" style={{ fontSize: 12.5 }}>{t("vat_rate")}: <strong>{vat.vat_pct}%</strong></div>
                )}
                <div style={{ ...CARD, padding: 14, overflowX: "auto" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("by_point")}</div>
                  <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={TH}>{t("point")}</th>
                      <th style={{ ...TH, ...TR }}>{t("taxable")}</th>
                      <th style={{ ...TH, ...TR }}>{t("exempt")}</th>
                      <th style={{ ...TH, ...TR }}>{t("tax_collected")}</th>
                      <th style={{ ...TH, ...TR }}>{t("gross")}</th>
                      <th style={{ ...TH, ...TR }}>{t("count")}</th>
                    </tr></thead>
                    <tbody>
                      {(vat.by_revenue_point || []).map((p) => (
                        <tr key={p.point}>
                          <td style={{ padding: "3px 6px", fontWeight: 600 }}>{p.point}</td>
                          <td style={TR}>{money(p.taxable_net)}</td>
                          <td style={TR}>{money(p.exempt_net)}</td>
                          <td style={TR}>{money(p.vat)}</td>
                          <td style={TR}>{money(p.gross)}</td>
                          <td style={TR}>{p.count}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                        <td style={{ padding: "4px 6px" }}>{t("totals")}</td>
                        <td style={TR}>{money((vat.totals || {}).taxable_net)}</td>
                        <td style={TR}>{money((vat.totals || {}).exempt_net)}</td>
                        <td style={TR}>{money((vat.totals || {}).vat)}</td>
                        <td style={TR}>{money((vat.totals || {}).gross)}</td>
                        <td style={TR}>{(vat.totals || {}).count}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
